import { UnauthenticatedError } from "@sp-crm/core";
import React, { FunctionComponent } from "react";
import {
    MutationCache,
    MutationKey,
    QueryCache,
    QueryClient,
    QueryClientProvider,
    QueryKey,
} from "react-query";
import { useDispatch } from "react-redux";
import { clientLogout } from "store/actions/session";
import {
    makeErrorFingerprint,
    reportError,
    shouldSkipReporting,
} from "util/error-tracking";

interface AppProps {
    children: (queryClient: QueryClient) => React.ReactNode;
}

const makeKey = (key: QueryKey | MutationKey): string => {
    return Array.isArray(key) ? key.join(":") : String(key ?? "unknown");
};

export const StoreAwareQueryClientProvider: FunctionComponent<AppProps> = ({
    children,
}) => {
    const dispatch = useDispatch();

    const queryClient = React.useMemo(
        () =>
            new QueryClient({
                queryCache: new QueryCache({
                    onError: (error, query) => {
                        if (error instanceof UnauthenticatedError) {
                            clientLogout(dispatch);
                        }

                        if (!shouldSkipReporting(error)) {
                            const err = error as Error;
                            const keyString = makeKey(query.queryKey);
                            reportError(err, {
                                fingerprint: makeErrorFingerprint(
                                    "query",
                                    keyString,
                                    error,
                                ),
                                params: { message: err?.message },
                                context: { queryKey: keyString },
                            });
                        }
                    },
                }),
                mutationCache: new MutationCache({
                    onError: (error, _variables, _context, mutation) => {
                        if (error instanceof UnauthenticatedError) {
                            clientLogout(dispatch);
                        }

                        if (!shouldSkipReporting(error)) {
                            const err = error as Error;
                            const keyString = makeKey(mutation.options.mutationKey);
                            reportError(err, {
                                fingerprint: makeErrorFingerprint(
                                    "mutation",
                                    keyString,
                                    error,
                                ),
                                params: { message: err?.message },
                                context: { mutationKey: keyString },
                            });
                        }
                    },
                }),
            }),
        [dispatch],
    );

    return (
        <QueryClientProvider client={queryClient}>
            {/* Useful for debugging react-query <ReactQueryDevtools initialIsOpen={false} /> */}
            {children(queryClient)}
        </QueryClientProvider>
    );
};
